import { useState, useEffect } from "react";
import { showSuccess, showError } from "@/lib/toastHelpers";
import { useQuery } from "@tanstack/react-query";
import { useApiMutation } from "@/hooks/useApiMutation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Settings2, Mail } from "lucide-react";

type AppSetting = { key: string; value: string; description: string | null; };

export default function AppSettings() {
  const [trialDuration, setTrialDuration] = useState("14");
  const [maxFileSize, setMaxFileSize] = useState("5");
  const [inboxFromEmail, setInboxFromEmail] = useState("support@chefaiq.com");
  const [inboxS3Bucket, setInboxS3Bucket] = useState("ses-bailiwickacademy-recieved-emails");
  const [inboxS3Prefix, setInboxS3Prefix] = useState("inbox/");

  const { data: settings = [] } = useQuery<AppSetting[]>({ queryKey: ["/api/admin/settings"] });

  useEffect(() => {
    const get = (key: string) => settings.find(s => s.key === key)?.value;
    if (get("trial_duration_days")) setTrialDuration(get("trial_duration_days")!);
    if (get("max_file_size_mb")) setMaxFileSize(get("max_file_size_mb")!);
    if (get("inbox_from_email")) setInboxFromEmail(get("inbox_from_email")!);
    if (get("inbox_s3_bucket")) setInboxS3Bucket(get("inbox_s3_bucket")!);
    if (get("inbox_s3_prefix")) setInboxS3Prefix(get("inbox_s3_prefix")!);
  }, [settings]);

  const updateSettingMutation = useApiMutation({
    endpoint: (vars: { key: string }) => `/api/admin/settings/${vars.key}`,
    method: "PATCH",
    invalidate: [["/api/admin/settings"]],
    successMessage: "Setting updated successfully",
    errorMessage: "Failed to update setting",
  });

  const save = (key: string, value: string) => updateSettingMutation.mutate({ key, value } as any);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-3xl font-bold tracking-tight">App Settings</h1>
        <p className="text-muted-foreground">Configure global application settings</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2"><Settings2 className="w-5 h-5" /><CardTitle>General Settings</CardTitle></div>
          <CardDescription>Manage application-wide configuration</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="trial-duration">Trial Duration (days)</Label>
            <div className="flex gap-2">
              <Input id="trial-duration" type="number" min="0" value={trialDuration} onChange={(e) => setTrialDuration(e.target.value)} className="max-w-[200px]" />
              <Button onClick={() => save("trial_duration_days", trialDuration)} disabled={updateSettingMutation.isPending}>Save</Button>
            </div>
            <p className="text-sm text-muted-foreground">Number of days users get for free trial access</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="max-file-size">Max File Upload Size (MB)</Label>
            <div className="flex gap-2">
              <Input id="max-file-size" type="number" min="1" max="100" value={maxFileSize} onChange={(e) => setMaxFileSize(e.target.value)} className="max-w-[200px]" />
              <Button onClick={() => save("max_file_size_mb", maxFileSize)} disabled={updateSettingMutation.isPending}>Save</Button>
            </div>
            <p className="text-sm text-muted-foreground">Maximum file size for image and CSV uploads</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2"><Mail className="w-5 h-5" /><CardTitle>Inbox Email Settings</CardTitle></div>
          <CardDescription>Configure the email address and S3 bucket used for the Admin Inbox (receiving replies from users)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="inbox-from">From / Reply-To Email Address</Label>
            <div className="flex gap-2">
              <Input id="inbox-from" type="email" value={inboxFromEmail} onChange={(e) => setInboxFromEmail(e.target.value)} className="max-w-sm" placeholder="support@chefaiq.com" />
              <Button onClick={() => save("inbox_from_email", inboxFromEmail)} disabled={updateSettingMutation.isPending}>Save</Button>
            </div>
            <p className="text-sm text-muted-foreground">Emails sent to users will come from this address. Replies from users will appear in the Inbox.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="inbox-bucket">S3 Bucket (for receiving emails via SES)</Label>
            <div className="flex gap-2">
              <Input id="inbox-bucket" value={inboxS3Bucket} onChange={(e) => setInboxS3Bucket(e.target.value)} className="max-w-sm" placeholder="your-ses-bucket" />
              <Button onClick={() => save("inbox_s3_bucket", inboxS3Bucket)} disabled={updateSettingMutation.isPending}>Save</Button>
            </div>
            <p className="text-sm text-muted-foreground">AWS S3 bucket where SES stores incoming emails. Must be configured in AWS SES → Email Receiving rules.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="inbox-prefix">S3 Prefix (folder path)</Label>
            <div className="flex gap-2">
              <Input id="inbox-prefix" value={inboxS3Prefix} onChange={(e) => setInboxS3Prefix(e.target.value)} className="max-w-sm" placeholder="inbox/" />
              <Button onClick={() => save("inbox_s3_prefix", inboxS3Prefix)} disabled={updateSettingMutation.isPending}>Save</Button>
            </div>
            <p className="text-sm text-muted-foreground">Folder prefix inside the S3 bucket where incoming emails are stored.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
